import {
  Container,
  AnimatedSprite,
  Sprite,
  Texture,
  Bounds,
  Rectangle,
} from "pixi.js";
import { StickTextures } from "./Textures";
import { playSound } from "../utils/sound";

type MarshmallowState =
  | "respawn"
  | "default"
  | "melt"
  | "more-melt"
  | "burning"
  | "charcoal";

type Stick = {
  container: Container;
  setIsMelting: (isMelting: boolean) => void;
  update: () => void;
  getHitArea: () => Rectangle;
};

const FRAMES_FOR_MELT = 180;
const FRAMES_FOR_BURNING = 360;
const FRAMES_FOR_CHARCOAL = 480;
const FRAMES_FOR_RESPAWN = 240;

export function createStick(textures: StickTextures): Stick {
  const container = new Container();
  const stickContainer = new Container();
  const hitArea = new Sprite(Texture.WHITE);
  hitArea.width = 20;
  hitArea.height = 20;
  hitArea.position.x = -70;
  hitArea.position.y = -44;
  hitArea.tint = 0xff0000;
  hitArea.visible = false;

  let isMelting = false;

  let state = "default";
  let timer = 0;

  stickContainer.position.x = -67;
  stickContainer.position.y = -44;

  container.addChild(stickContainer);
  container.addChild(hitArea);

  const setIsMelting = (_isMelting: boolean) => {
    if (isMelting !== _isMelting) {
      isMelting = _isMelting;
    }
  };

  const getHitArea = () => {
    hitArea.visible = true;
    const b = hitArea.getFastGlobalBounds().rectangle;
    hitArea.visible = false;
    return b;
  };

  const updateStickGfx = () => {
    stickContainer.removeChildren();
    let stick: AnimatedSprite | Sprite;
    if (state === "burning") {
      stick = new AnimatedSprite(textures.fire);
      (stick as AnimatedSprite).animationSpeed = 0.0835;
      (stick as AnimatedSprite).play();
    } else if (state === "respawn") {
      stick = new AnimatedSprite(textures.respawn);
      (stick as AnimatedSprite).animationSpeed = 0.0835;
      (stick as AnimatedSprite).play();
    } else {
      let texture = textures.default;
      if (state === "melt") {
        texture = textures.melt;
      } else if (state === "moreMelt") {
        texture = textures.moreMelt;
      } else if (state === "charcoal") {
        texture = textures.charcoal;
      }
      stick = new Sprite(texture);
    }
    stickContainer.addChild(stick);
  };

  const update = () => {
    if (
      isMelting ||
      state === "burning" ||
      state === "respawn" ||
      state === "charcoal"
    ) {
      timer++;
    }

    if (state === "default" && timer > FRAMES_FOR_MELT) {
      timer = 0;
      state = "melt";
      updateStickGfx();
      return;
    }

    if (state === "melt" && timer > FRAMES_FOR_MELT) {
      timer = 0;
      state = "moreMelt";
      updateStickGfx();
      return;
    }

    if (state === "moreMelt" && timer > FRAMES_FOR_MELT) {
      timer = 0;
      state = "burning";
      updateStickGfx();
      playSound("/burn.wav");
      return;
    }

    if (state === "burning" && timer > FRAMES_FOR_BURNING) {
      timer = 0;
      state = "charcoal";
      updateStickGfx();
      return;
    }

    if (state === "charcoal" && timer > FRAMES_FOR_CHARCOAL) {
      timer = 0;
      state = "respawn";
      updateStickGfx();
      return;
    }

    if (state === "respawn" && timer > FRAMES_FOR_RESPAWN) {
      timer = 0;
      state = "default";
      updateStickGfx();
      return;
    }
  };

  updateStickGfx();
  return { container, update, getHitArea, setIsMelting };
}
